import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { OtpService } from '@app/services/authenticators/otp.service';
import { CertificateService } from '@app/services/certificates/certificate.service';
import { MultiCertService } from '@app/services/certificates/multi-cert.service';
import { SessionService } from '@app/services/session/session.service';
import { UserCertificate } from '@app/store/models';
import { UserInfo, UserSession } from '@app/store/models/session.model';
import { GlobalState } from '@app/store/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import {
  FormlyFieldConfig,
  FormlyFormOptions,
} from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from 'ngx-webstorage';
import { lastValueFrom, tap } from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'app-force-pwd',
    templateUrl: './force-pwd.component.html',
    styleUrls: ['./force-pwd.component.scss'],
    standalone: false
})
export class ForcePWDPageComponent implements OnInit {
  constructor(
    private sessionService: SessionService,
    private certificateService: CertificateService,
    private localStorageService: LocalStorageService,
    private otpService: OtpService,
    private toasterService: ToastrService,
    private translateService: TranslateService,
    private store: Store<GlobalState>,
    private router: Router,
    private multiCertService: MultiCertService
  ) {}

  loading = false;
  done = false;
  isEseal = false;
  showPuk = true;
  isPkBox = false;
  isTrissUser = false;

  showStep1: any = false;
  session: UserSession | undefined;
  certificate: UserCertificate | undefined;

  form = new UntypedFormGroup({});

  formOptions: FormlyFormOptions = {
    formState: { submitAttempted: false },
  };

  model = { puk: '', newPassword: '', confirmPassword: '' };

  fields: FormlyFieldConfig[];

  createFields(isPkBox: boolean = false, user: UserInfo): FormlyFieldConfig[] {
    const fields: FormlyFieldConfig[] = [
      {
        fieldGroup: [
          {
            fieldGroupClassName: 'formly-group ic1-row',
            validators: {
              validation: [
                {
                  name: 'SAME_PASSWORD',
                  options: { errorPath: 'confirmPassword' },
                },
              ],
            },
            fieldGroup: [
              {
                key: 'newPassword',
                type: 'ic1-text',
                className: 'ic1-col-12 ic1-col-lg-6',

                validators: {
                  validation: [
                    { name: 'PASSWORD_STRENGHT', options: { user } },
                  ],
                },

                props: {
                  type: 'password',
                  label: 'ACTIVATION_STEPS_PAGE.FORCE_PWD_STEP.NEW_PWD_LABEL',
                  placeholder:
                    'ACTIVATION_STEPS_PAGE.FORCE_PWD_STEP.NEW_PWD_PLACEHOLDER',
                  required: true,
                  minLength: 8,
                },
              },
              {
                key: 'confirmPassword',
                type: 'ic1-text',
                className: 'ic1-col-12 ic1-col-lg-6',
                props: {
                  type: 'password',
                  label:
                    'ACTIVATION_STEPS_PAGE.FORCE_PWD_STEP.CONFIRM_PWD_LABEL',
                  placeholder:
                    'ACTIVATION_STEPS_PAGE.FORCE_PWD_STEP.CONFIRM_PWD_PLACEHOLDER',
                  required: true,
                  minLength: 8,
                },
              },
              {
                key: 'puk',
                type: 'ic1-text',
                className: 'ic1-col-12 ic1-col-lg-6',
                expressions: {
                  hide: (field: FormlyFieldConfig) => {
                    return !this.showPuk;
                  },
                },
                props: {
                  type: 'text',
                  label: 'ACTIVATION_STEPS_PAGE.FORCE_PWD_STEP.PIN_PUK_LABEL',
                  placeholder:
                    'ACTIVATION_STEPS_PAGE.FORCE_PWD_STEP.PIN_PUK_PLACEHOLDER',
                  required: true,
                  minLength: 8,
                  maxLength: 8,
                  OnlyNumbers: 8,
                },
              },
            ],
          },
        ],
      },
    ];

    return fields;
  }

  ngOnInit(): void {
    console.log('🚀 ~ ForcePWDPageComponent init');

    this.formOptions.showError = (field: FormlyFieldConfig) => {
      if (field.hide) return false;
      const key = field.key as string;
      // Solo mismatch conferma password: visibile anche in digitazione
      if (
        key === 'confirmPassword' &&
        field.formControl?.invalid &&
        field.formControl?.errors?.['SAME_PASSWORD'] &&
        (this.model.confirmPassword?.length ?? 0) >= 8
      ) {
        return true;
      }
      const st = this.formOptions.formState as { submitAttempted?: boolean };
      if (!st?.submitAttempted) return false;
      return !!(field.formControl?.invalid);
    };

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      const st = this.formOptions.formState as { submitAttempted?: boolean };
      if (st?.submitAttempted) {
        st.submitAttempted = false;
      }
    });

    this.store
      .pipe(
        untilDestroyed(this),
        tap(store => {
          if (store.session) {
            this.session = store.session;
            this.isEseal = this.session.eseal;
            this.showStep1 = !!this.session.needsForcePassword;

            this.isPkBox =
              !store.session?.icssUser && !store.session?.trissUser;

            this.isTrissUser = store.session?.trissUser;

            this.fields = this.createFields(
              this.isPkBox,
              store.session.userInfo
            );

            this.hidePuk();
          }
        })
      )
      .subscribe();
  }

  hidePuk() {
    // Se eSeal o (ICSS e FA) il puk non serve
    if (
      this.session.eseal ||
      this.session.icssUser ||
      this.session?.trissUser ||
      this.session.isacUser
    ) {
      this.showPuk = false;
    }
  }

  onSubmit() {
    this.loading = true;
    // stop here if form is invalid
    if (this.form.invalid) {
      (this.formOptions.formState as { submitAttempted: boolean }).submitAttempted =
        true;
      this.form.markAllAsTouched();
      this.loading = false;
      return;
    }
    let puk = this.model.puk.trim();

    if (
      this.session?.icssUser ||
      this.session?.trissUser ||
      this.session?.isacUser
    ) {
      puk = null;
    } else if (this.isEseal) {
      puk = '11223344';
    }

    this.sessionService.forcePWD(puk, this.model.newPassword.trim()).subscribe({
      next: async response => {
        console.log('🚀 ~ ForcePWDComponent ~ onSubmit ~ response', response);

        this.loading = false;
        // Se tutto va bene
        this.localStorageService.store('otpSent', true);
        this.showStep1 = false;

        //this.localStorageService.store('puk', puk);
        // Aggiorna i dati della session
        this.sessionService.getUserSession().then(async session => {
          this.session = session;
          this.localStorageService.store('session', session);

          const certificates = await lastValueFrom(
            this.certificateService.getCertificateList()
          );

          const signers = certificates.map(obj => obj.certId);
          this.multiCertService.setSigners(signers);

          this.certificateService.certID = certificates?.[0].certId;

          if (certificates.length > 1) {
            // MULTICERT
            this.router.navigate(['/multi-cert']);
            this.localStorageService.store('isMulticert', true);
          } else {
            this.certificateService
              .getPromiseCertificate()
              .then(certificate => {
                this.certificate = certificate;
                this.localStorageService.store('certificate', certificate);
                // Serve lo step 2
                if (this.localStorageService.retrieve('needActivation')) {
                  // prendo la lista di certificati che hanno bisogno dell'attivazione
                  let activationList =
                    this.localStorageService.retrieve('needActivation');

                  // aggiungo il certificato loggato nella lista
                  if (!activationList.includes(certificate.certId)) {
                    activationList.push(certificate.certId);
                    this.localStorageService.store(
                      'needActivation',
                      activationList
                    );
                  }
                } else {
                  // siccome non esiste la lista, creo la lista e aggiungo il certificato come primo elemento
                  this.localStorageService.store('needActivation', [
                    certificate.certId,
                  ]);
                }
                this.localStorageService.store('certificate', certificate);

                // bug fix FA: reload forzato per aggiornare i campi sul componente first-emis
                this.localStorageService.clear('otpSent');
                window.location.reload();
              });
          }
        });

        /* this.toasterService.success(
          this.translateService.instant(
            'ACTIVATION_STEPS_PAGE.FORCE_PWD_STEP.SUCCESS.DESC'
          ),
          this.translateService.instant(
            'ACTIVATION_STEPS_PAGE.FORCE_PWD_STEP.SUCCESS.TITLE'
          ),
          {
            positionClass: 'toast-top-right',
          }
        ); */
      },
      error: error => {
        console.error(error);
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  clearForm() {
    this.form.reset();
    (this.formOptions.formState as { submitAttempted: boolean }).submitAttempted =
      false;
    if (
      this.isEseal ||
      this.session?.icssUser ||
      this.session?.trissUser ||
      this.session?.isacUser
    ) {
      this.showPuk = false;
    }
  }
}
